'use client';
import { useMedia } from '@ssv-website/hooks';
import { Logo } from '../../../logo';
import { CosImage } from '../../../cos-image';
import public_account from './images/public-account.png';
import video_code from './images/video-number.png';
import follow_wechat from '../../../footer/images/follow-wechat.svg';
import follow_video from '../../../footer/images/follow-video.svg';
import logo_icon from './images/footer-logo.png';
import intl_logo_icon from './images/intl-ssv-footer-logo.png';
import SSVLink from '../../../ssv-link';
import { LinkHref } from '../../../navbar/config';
import { useLocale, useTranslations } from 'use-intl';
import { useIsExternalDomain } from '../../s-2025/context/MeetingConfigContext';

const ASSET_PATH = {
  logo: 'web-ui/ssv-metting/components/SSVFooter/images/footer-logo.png',
  logoIntl:
    'web-ui/ssv-metting/components/SSVFooter/images/intl-ssv-footer-logo.png',
  videoCode: 'web-ui/ssv-metting/components/SSVFooter/images/video-number.png',
  publicAccount:
    'web-ui/ssv-metting/components/SSVFooter/images/public-account.png',
} as const;

export default function SSVFooter(props: {
  className?: string;
  bgColor?: string;
  bgImage?: string;
  bgImage_m?: string;
}) {
  const {
    className = '',
    bgColor = '#647DFF',
    bgImage = 'https://cdn-digital.ssv.qq.com/upload//s2025/s-footer-bg2.png',
    bgImage_m = 'https://cdn-digital.ssv.qq.com/upload//s2025/s-footer-bg-m.png',
  } = props;
  const isMobile = useMedia('mobile');
  const locale = useLocale();
  const t = useTranslations('ssvFooter');
  const _bgImage = isMobile ? bgImage_m : bgImage;
  return (
    <div
      id="Footer"
      className={`w-full flex justify-center bg-no-repeat bg-cover ${className}`}
      style={{
        backgroundImage: _bgImage ? `url(${_bgImage})` : '',
        backgroundColor: bgColor,
        backgroundPosition: !isMobile ? '60% 0' : '0 30%',
        color: '#ffffff',
      }}
    >
      <div className="max-w-5xl flex flex-col w-full desktop:px-5">
        <div className="desktop:pr-5">
          {locale === 'en' ? (
            <Logo
              className="w-[250px] h-[40px] mobile:hidden mt-9"
              src={intl_logo_icon}
              path={ASSET_PATH.logoIntl}
            />
          ) : (
            <Logo
              className="w-[160px] h-[38px] mobile:hidden mt-9"
              src={logo_icon}
              path={ASSET_PATH.logo}
            />
          )}

        </div>
        <div className="flex justify-between mt-20 mobile:flex-col-reverse mobile:mt-16 mobile:gap-9">
          <FooterList isMobile={isMobile} />
          <div className="hidden mobile:block">
            <DimensionalCode />
          </div>
        </div>
        <FootNote />
      </div>
    </div>
  );
}

const FooterList = (props: { isMobile: boolean }) => {
  const { isMobile } = props;
  const t = useTranslations('ssvFooter');
  const footerList = [
    {
      title: t('腾讯SSV'),
      list: [
        // { text: '开放平台', path: '' },
        { text: t('案例库'), path: LinkHref.Case },
        { text: t('报告'), path: LinkHref.Report },
        { text: t('品牌规范'), path: LinkHref.DesignAssets },
        // { text: '关于SSV', path: LinkHref.AboutSSV },
      ],
    },
    {
      title: t('更多科技向善'),
      list: [
        { text: t('腾讯公益'), path: 'https://gongyi.qq.com/' },
        { text: t('腾讯基金会'), path: 'https://www.tencentfoundation.org/' },
      ],
    },
    {
      title: t('了解公司'),
      list: [
        {
          text: t('关于腾讯'),
          path: 'https://www.tencent.com/zh-cn/about.html#about-con-1',
        },
        { text: t('加入我们'), path: 'https://careers.tencent.com/' },
      ],
    },
    {
      title: t('法律信息'),
      list: [
        {
          text: t('服务协议'),
          path: 'https://www.tencent.com/zh-cn/service-agreement.html',
          path_en:
            'https://cdn-digital.ssv.qq.com/ssv_privacy/Tencent-SSV-Website-Terms-of-Service.html',
        },
        {
          text: t('隐私政策'),
          path: 'https://cdn-digital.ssv.qq.com/ssv_privacy/privacy_service.html',
          path_en:
            'https://cdn-digital.ssv.qq.com/ssv_privacy/en-ssv-privacy.html',
        },
        { text: t('知识产权'), path: 'https://ipr.tencent.com/' },
      ],
    },
  ];
  const locale = useLocale();
  const isExternalDomain = useIsExternalDomain();
  const list = isExternalDomain ? footerList.slice(3) : footerList;

  return (
    <div
      className={`flex flex-1 mobile:justify-evenly ${
        isExternalDomain ? 'gap-[200px] mobile:gap-0' : ''
      }`}
    >
      {list.map((item, index) => (
        <div key={index} className={isMobile ? '' : 'w-[200px] flex-none'}>
          <div
            className={`font-600 text-base mb-5 whitespace-nowrap mobile:text-sm mobile:mb-3`}
          >
            {item.title}
          </div>
          <div className={isMobile ? '' : 'flex flex-col gap-[10px]'}>
            {item.list.map((v, i) => {
              const url = locale === 'en' ? v.path_en : v.path;
              return (
                <SSVLink url={url || v.path} key={v.text + i} isBlank>
                  <div
                    className={`font-400 opacity-80 cursor-pointer whitespace-nowrap hover:opacity-100 hover:font-500 ${
                      isMobile
                        ? 'text-sm leading-none mb-3'
                        : 'text-base leading-[22px]'
                    }`}
                  >
                    {v.text}
                  </div>
                </SSVLink>
              );
            })}
          </div>
        </div>
      ))}
      <FollowUs title={t('关注我们')} />
    </div>
  );
};

const FollowUs = (props: { title: string }) => {
  const { title } = props;
  return (
    <div className="w-[200px] flex-none mobile:hidden">
      <div className="font-600 text-base mb-5">{title}</div>
      <div className="flex flex-col gap-[10px]">
        <FollowCode
          code={video_code}
          path={ASSET_PATH.videoCode}
          icon={follow_video}
        />
        <FollowCode
          code={public_account}
          path={ASSET_PATH.publicAccount}
          icon={follow_wechat}
        />
      </div>
    </div>
  );
};

const FollowCode = (props: {
  code: typeof video_code;
  path: string;
  icon: typeof follow_video;
}) => {
  const { code, path, icon } = props;
  return (
    <div className="group relative h-[22px] w-[22px] cursor-pointer">
      <img
        src={icon.src}
        alt=""
        className="h-[22px] w-[22px] opacity-80 transition-opacity group-hover:opacity-100"
      />
      <div className="absolute bottom-[28px] left-0 z-50 hidden h-[148px] w-[148px] rounded-tl-xl rounded-tr-xl rounded-br-xl bg-white p-[10px] group-hover:block">
        <CosImage
          fallbackSrc={code}
          path={path}
          alt=""
          className="h-[129px] w-[129px] object-cover"
        />
      </div>
    </div>
  );
};

const DimensionalCode = () => {
  const t = useTranslations('ssvFooter');
  return (
    <div className="flex gap-2 justify-center">
      <div className="font-400">
        <CosImage
          fallbackSrc={video_code}
          path={ASSET_PATH.videoCode}
          alt=""
          className={`object-cover w-32 h-32`}
        />
        <div className={`text-center opacity-40 mt-1 text-sm  w-32`}>
          {t('关注官方视频号')}{' '}
        </div>
      </div>
      <div className="font-400">
        <CosImage
          fallbackSrc={public_account}
          path={ASSET_PATH.publicAccount}
          alt=""
          className={`object-cover w-32 h-32`}
        />
        <div className={`text-center w-32 opacity-40 mt-1 text-sm`}>
          {t('关注官方公众号')}
        </div>
      </div>
    </div>
  );
};
const FootNote = () => {
  const year = new Date().getFullYear();
  return (
    <div
      className={`opacity-40 w-full text-base mobile:text-xss mb-5 mt-16 mobile:mt-12 mobile:mb-5 mobile:text-center`}
    >
      Copyright © 1998 - {year} Tencent. All Rights Reserved.
    </div>
  );
};
